// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Landbridge — turning a seed into a route (start + destination) or a chain start,
// deterministically. This is the whole P2P-sync surface: every peer feeds the
// frozen lobby seed through here and gets the byte-identical puzzle, so nothing
// about the route ever crosses the wire.

import { makeRng, randInt, hashSeed, shuffle, type Rng } from '@ben-gy/game-engine/rng';
import { ENDPOINTS, bfsFrom, neighbors, type Country } from './data';
import type { Mode } from './modes';

/** Today's date in UTC as YYYY-MM-DD — the same string for every player on Earth. */
export function todayUTC(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10);
}

/** The daily seed for a mode: identical worldwide, changes at UTC midnight. */
export function dailySeed(mode: Mode, dateStr: string): number {
  return hashSeed(`landbridge:${mode.id}:${dateStr}`);
}

export interface Pair {
  startId: string;
  destId: string;
  /** BFS optimal hop count (par). */
  par: number;
}

/**
 * A solvable (start, destination) whose optimal route length falls in the mode's
 * par range. Deterministic: the seed shuffles the endpoint list, then we take the
 * first start that has at least one in-range destination and pick one by seed.
 * Because both big landmasses have many endpoints at any sane par, this resolves
 * on the first or second start every time — but it is written to always terminate
 * with a valid, provably reachable pair.
 */
export function pairFor(mode: Mode, seed: number): Pair {
  const rng = makeRng(seed >>> 0);
  const starts = shuffle(rng, ENDPOINTS.slice());
  for (const start of starts) {
    const dist = bfsFrom(start.id);
    const dests = ENDPOINTS.filter((c) => {
      if (c.id === start.id) return false;
      const d = dist.get(c.id);
      return d !== undefined && d >= mode.parMin && d <= mode.parMax;
    });
    if (dests.length) {
      const dest = dests[randInt(rng, 0, dests.length - 1)];
      return { startId: start.id, destId: dest.id, par: dist.get(dest.id)! };
    }
  }
  // Unreachable in practice (asserted by the balance sim). Fall back to the
  // farthest reachable endpoint from the first start so we never return garbage.
  const start = starts[0];
  const dist = bfsFrom(start.id);
  let best: Country = start;
  let bestD = -1;
  for (const c of ENDPOINTS) {
    const d = dist.get(c.id);
    if (c.id !== start.id && d !== undefined && d > bestD) {
      bestD = d;
      best = c;
    }
  }
  return { startId: start.id, destId: best.id, par: Math.max(1, bestD) };
}

/**
 * A chain-mode start: a well-known country with a decent branching factor, so the
 * opening is not a dead end. Deterministic from the seed.
 */
export function chainStartFor(_mode: Mode, seed: number): Country {
  const rng: Rng = makeRng(seed >>> 0);
  const good = ENDPOINTS.filter((c) => neighbors(c.id).length >= 3);
  const pool = good.length ? good : ENDPOINTS.slice();
  return pool[randInt(rng, 0, pool.length - 1)];
}
